import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import { parse } from 'query-string';
import cn from 'classnames';
import debounce from 'lodash.debounce';
import ExecutionEnvironment from 'exenv';

import { PRODUCT_PAGE } from 'constants/amethystPageTypes';
import {
  HYDRA_BUY_NOW_BUTTON,
  HYDRA_LAST_SELECTED_SIZE,
  HYDRA_OOSM,
  HYDRA_PDP_SIZECHART,
  HYDRA_PLACEBO_PDP,
  HYDRA_PRODUCT_CALLOUT,
  HYDRA_SIZE_RECOMMENDER_TOOLTIP,
  HYDRA_SOCIAL_COLLECTIONS
} from 'constants/hydraTests';
import { NO_VIZ } from 'constants/productRecoTypes';
import { pvProduct } from 'events/product';
import { evAddToCart, evBuyNow, evProceedToCheckout } from 'events/cart';
import { evAddToFavorites } from 'events/favorites';
import { evRecommendationClick } from 'events/recommendations';

import { fetchAsk } from 'actions/ask';
import { getHeartCounts, getHearts, heartProduct, toggleHeartingLoginModal, unHeartProduct } from 'actions/hearts';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { getHeartProps } from 'helpers/HeartUtils';
import { fetchCustomerAuthDetails } from 'actions/authentication';
import { pageTypeChange } from 'actions/common';
import { isAssigned, isInAssignment, triggerAssignment } from 'actions/ab';
import { onLookupRewardsTransparencyPointsForItem } from 'store/ducks/rewards/actions';
import { buildProductPageRecoKey, getRecosSlot } from 'helpers/RecoUtils';
import {
  addItemToCart,
  fetchBrandPromo,
  fetchProductReviews,
  fetchProductSearchSimilarity,
  fetchSizingPrediction,
  hideSelectSizeTooltip,
  highlightSelectSizeTooltip,
  loadProductDetailPage,
  productSizeChanged,
  productSwatchChange,
  setCarouselIndex,
  setProductDocMeta,
  showSelectSizeTooltip,
  togglePdpVisibility,
  toggleProductDescription,
  unhighlightSelectSizeTooltip,
  validateDimensions
} from 'actions/productDetail';
import {
  fetchProductReviewsWithMedia,
  showReviewGalleryModal
} from 'actions/reviews';
import { toggleBrandNotifyModal, toggleProductNotifyModal, toggleReportAnErrorModal } from 'actions/productdetail/sharing';
import { setLastSelectedSize } from 'actions/lastSelectedSizes';
import { submitNotifyBrandEmail } from 'actions/brand';
import { changeQuantity, navigateToCheckout, showCartModal } from 'actions/cart';
import { fetchProductPageRecos } from 'actions/recos';
import { fetchRelatedProducts } from 'actions/relatedProducts';
import { translateCartError } from 'apis/mafia';
import marketplace from 'cfg/marketplace.json';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import AddToFavoritesButton from 'components/productdetail/AddToFavoritesButton';
import BuyNowButton from 'components/productdetail/BuyNowButton';
import SocialCollectionsWidget from 'components/account/SocialCollectionsWidget';
import DontForget from 'components/productdetail/DontForget';
import ProductBreadcrumbs from 'components/productdetail/ProductBreadcrumbs';
import ProductCallout from 'components/productdetail/ProductCallout';
import ProductNotifyModal from 'components/productdetail/sharing/ProductNotifyModal';
import BrandStylesNotifyModal from 'components/productdetail/sharing/BrandStylesNotifyModal';
import ReportAnErrorModal from 'components/productdetail/sharing/ReportAnErrorModal';
import StylePicker from 'components/productdetail/stylepicker/StylePicker';
import MobileSwatchPicker from 'components/productdetail/MobileSwatchPicker';
import ProductImages from 'components/productdetail/ProductImages';
import ProductSummary from 'components/productdetail/ProductSummary';
import Ask from 'containers/Ask';
import ExpandableProductDescription from 'components/productdetail/description/ExpandableProductDescription';
import RecosDetail1 from 'components/productdetail/RecosDetail1';
import RecosDetail2 from 'components/productdetail/RecosDetail2';
import RecosDetail3 from 'components/productdetail/RecosDetail3';
import ReviewPreview from 'components/productdetail/ReviewPreview';
import SizeGroups from 'components/productdetail/sizegroups/SizeGroups';
import { indefiniteArticleSelector, isExternalReferrer, openPopup } from 'helpers';
import ProductUtils from 'helpers/ProductUtils';
import { isDesktop, offset } from 'helpers/ClientUtils';
import { buildSeoBrandString, buildSeoProductString, buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import { parsePath, relativizeUrl } from 'helpers/LocationUtils';
import FitSurvey from 'components/productdetail/FitSurvey';
import FacebookShare from 'components/productdetail/sharing/FacebookShare';
import TwitterShare from 'components/productdetail/sharing/TwitterShare';
import PinterestShare from 'components/productdetail/sharing/PinterestShare';
import EmailShare from 'components/productdetail/sharing/EmailShare';
import SmsShare from 'components/productdetail/sharing/SmsShare';
import Price from 'components/productdetail/Price';
import SessionRequired from 'components/common/SessionRequired';
import ShippingAndReturnsBanner from 'components/productdetail/ShippingAndReturnsBanner';
import OutOfStockPopover from 'components/productdetail/OutOfStockPopover';
import OutOfStockModal from 'components/productdetail/OutOfStockModal';
import ReviewPhotoGallery from 'components/productdetail/ReviewPhotoGallery';
import ReviewGallery from 'containers/ReviewGallery';
import BrandPromo from 'components/productdetail/BrandPromo';
import MelodyVideoPlayer from 'components/common/melodyVideo/MelodyVideoPlayer';
import { POPUP_CLASS_RE, PRODUCT_DETAIL_IMAGE_PAGE_RE } from 'common/regex';
import { shouldRenderReviewGallery } from 'helpers/ReviewUtils';
import { getNumberOfAskQuestions } from 'helpers/AskUtils';
import { onEvent } from 'helpers/EventHelpers';
import { titaniteView, track } from 'apis/amethyst';
import { stockSelectionCompleted } from 'store/ducks/productDetail/actions';
import ImageLazyLoader from 'components/common/ImageLazyLoader';
import SingleSizeChart from 'components/common/SingleSizeChart';
import { VideoObject } from 'components/common/StructuredDataTypes';
import { getApproximateSize, getLastSelectedSize } from 'reducers/lastSelectedSizes';
import css from 'styles/containers/productDetail.scss';

const {
  desktopBaseUrl,
  features: {
    showAsk: showAskConstant,
    showDontForget: showDontForgetConstant,
    showSizeGroups: showSizeGroupsConstant,
    showReviews,
    showOOSNotifyMe: showOOSNotifyMeFeature
  },
  hasHearting,
  links,
  recos: { showSimilarItemsYouMayLike, showCustomersWhoViewedThisItemAlsoViewed, showRecosOnTopForExternalReferrer }
} = marketplace;

// map of the component prop name to hydra test name for ab tests the component is built for.
const TEST_PROP_TO_NAME = {
  hydraSizeRecommenderTooltip : HYDRA_SIZE_RECOMMENDER_TOOLTIP,
  hydraOosm: HYDRA_OOSM,
  hydraPlaceboPDP: HYDRA_PLACEBO_PDP,
  hydraProductCallout: HYDRA_PRODUCT_CALLOUT,
  hydraSocialCollections: HYDRA_SOCIAL_COLLECTIONS,
  hydraBuyNow: HYDRA_BUY_NOW_BUTTON,
  hydraSizeChart: HYDRA_PDP_SIZECHART,
  hydraLastSelectedSize : HYDRA_LAST_SELECTED_SIZE
};

// requires productId from path param with optional colorId from path.
export class ProductDetail extends Component {

  static beforeFetchDataOnServer({ dispatch }) {
    dispatch(triggerAssignment(HYDRA_BUY_NOW_BUTTON));
  }

  static fetchDataOnServer(store, location, { productId, colorId, seoName }) {
    const { dispatch } = store;
    return dispatch(loadProductDetailPage(productId, { colorId, seoName, firePixel: true }));
  }

  constructor(props) {
    super(props);
    this.onStockChange = this.onStockChange.bind(this);
    this.onProductDescriptionToggle = this.onProductDescriptionToggle.bind(this);
    this.onAddToFavorites = this.onAddToFavorites.bind(this);
    this.onRecoClicked = this.onRecoClicked.bind(this);
    this.onSwatchStyleChosen = this.onSwatchStyleChosen.bind(this);
    this.onHideSelectSizeTooltip = this.onHideSelectSizeTooltip.bind(this);
    this.unhighlightAndHideSelectSizeTooltip = this.unhighlightAndHideSelectSizeTooltip.bind(this);
    this.shouldShowExternalReferrerRecos = this.shouldShowExternalReferrerRecos.bind(this);
    this.makeProductNotifyMe = this.makeProductNotifyMe.bind(this);
    this.getHeartCountsDebounced = debounce(this.getHeartCountsDebounced.bind(this), 350);
    this.checkForHearts = this.checkForHearts.bind(this);
    this.getRecosToDisplay = this.getRecosToDisplay.bind(this);
  }

  state = {
    isSpotlightActive: false,
    externalReferrer: false,
    spotlightLowResImageSrc: '',
    spotlightHiResImageSrc: '',
    spotlightHiResImageWidth: 0,
    spotlightHiResImageHeight: 0,
    spotlightWrapperHeight: 0,
    mouseCoordinates: {},
    movementRatioX: 0,
    movementRatioY: 0
  };

  componentDidMount() {
    titaniteView();
    const { getHearts, pageTypeChange, pdpConfig } = this.props;

    pageTypeChange('product');

    const {
      lastSelectedSizes,
      product: { detail = {}, reviewData, sizingPredictionId },
      params: { productId, colorId, seoName },
      fetchBrandPromo,
      fetchAsk,
      fetchProductReviews,
      fetchProductReviewsWithMedia,
      fetchRelatedProducts,
      fetchSizingPrediction,
      isCustomer,
      location,
      setLayoutOption,
      setProductDocMeta,
      showAsk,
      showDontForget,
      showReviews,
      showSizeGroups,
      togglePdpVisibility,
      toggleBrandNotifyModal,
      toggleProductNotifyModal,
      toggleReportAnErrorModal,
      trackEvent,
      onLookupRewardsTransparencyPointsForItem,
      isGiftCard,
      hydraLastSelectedSize
    } = this.props;
    const { styles, defaultProductType, productId: detailProductId } = detail;
    // Tests triggered client side on willMount don't get updated props for the initial didMount
    // Instead we need to manually store the result of the assignment for checks done during didMount
    // See https://github.com/reactjs/react-redux/issues/210 for context.
    this.assignTests();
    const isDesktopSize = isDesktop(marketplace);

    // Setting event watcher for popup link clicks set by content
    onEvent(document.body, 'click', e => {
      const { target } = e;
      if (target && target.tagName === 'A' && POPUP_CLASS_RE.test(target.className)) {
        openPopup(e);
      }
    }, null, this);

    // if we client routed we need to load everything
    if (!detail || detailProductId !== productId || (styles && styles.length === 0)) {
      this.fetchData(productId);
    } else if (detail && styles) {
      setProductDocMeta(detail, colorId);
      !isGiftCard && onLookupRewardsTransparencyPointsForItem();
      // product data is loaded, but non-critical pieces may not
      if (window.pageYOffset === 0 && !isDesktop(marketplace)) {
        this.slideUpHeader();
      }

      if (showSizeGroups || showDontForget) {
        fetchRelatedProducts(productId);
      }

      if (showAsk) {
        fetchAsk({ productId });
      }

      if (showReviews && (!reviewData || reviewData.productId !== productId || reviewData.page !== 1 || reviewData.offset !== 0)) {
        fetchProductReviews(productId, 1, 0, false);
        fetchProductReviewsWithMedia(productId);
      }

      const style = ProductUtils.getStyleByColor(detail.styles, colorId);

      if (detailProductId === productId && seoName !== buildSeoProductString(detail, colorId)) {
        this.useSeoUrl(detail, colorId);
      }

      if (pdpConfig.showSizingPrediction && !sizingPredictionId && ProductUtils.isShoeType(defaultProductType)) {
        fetchSizingPrediction(productId, colorId);
      }

      if (isDesktopSize) { // desktop-only features
        if (pdpConfig.showBrandPromo && detail.brandId) {
          fetchBrandPromo(detail.brandId);
        }
      }
      track(pvProduct, style);
    }
    // we checkForRecos in the callback so that when we checkForRecos we can refer to this.state.externalReferrer and know it is accurate.
    this.setState({
      externalReferrer: isDesktopSize && isExternalReferrer()
    }, () => this.checkForRecos(defaultProductType, detailProductId, styles, { colorId }));
    trackEvent('TE_PV_PDP', productId);
    setLayoutOption('showBackToTop');
    togglePdpVisibility(true);

    if (location.hash) {
      if (location.hash.indexOf('showModal=notifyoos') > -1) {
        toggleProductNotifyModal(true);
      } else if (location.hash.indexOf('showModal=notifybrand') > -1) {
        toggleBrandNotifyModal(true);
      } else if (location.hash.indexOf('showModal=reportanerror') > -1) {
        toggleReportAnErrorModal(true);
      }
    }

    // Get hearting list
    if (isCustomer) {
      const heartOnLoad = location.query?.heartOnLoad || null;
      getHearts(heartOnLoad);
    }
    const lastSelectedSizesDoneLoading = lastSelectedSizes && Boolean(Object.keys(lastSelectedSizes).length);
    const shouldSetApproximateSizes = detail.sizing && lastSelectedSizesDoneLoading;
    if (hydraLastSelectedSize && pdpConfig.autoSelectApproximateSizes && shouldSetApproximateSizes) {
      this.handleSetApproximateSizes();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      params,
      trackEvent,
      product: { detail },
      pdpConfig,
      showCartModal,
      params: {
        colorId
      }
    } = prevProps;

    const {
      productId
    } = detail || {};

    const {
      isCartModalShowing,
      product: { isSimilarStylesLoading },
      params: {
        productId: nextProductId,
        colorId: nextColorId,
        seoName: nextSeoName
      },
      product: { detail: nextDetail },
      fetchSizingPrediction,
      fetchBrandPromo,
      hydraLastSelectedSize,
      lastSelectedSizes,
      isGiftCard,
      onLookupRewardsTransparencyPointsForItem
    } = this.props;

    const {
      productId: nextDetailProductId,
      styles: nextStyles,
      defaultProductType: nextProductType
    } = nextDetail || {};

    if (nextProductId !== params.productId) {
      this.fetchData(nextProductId);
      trackEvent('TE_PV_PDP', nextProductId);
    }

    const newProductLoaded = nextDetail && ((productId !== nextDetailProductId) || !detail);

    // update pretty URL if the product data for a new product has loaded
    if (nextDetail && nextDetailProductId === nextProductId) {

      if (newProductLoaded && buildSeoProductString(nextDetail, nextColorId) !== nextSeoName) {
        this.useSeoUrl(nextDetail, nextColorId);
      }

      // if the color changed but the product didn't, fetch new recos.
      if (!newProductLoaded && colorId !== nextColorId) {
        isCartModalShowing && showCartModal(false);
        this.checkForRecos(nextProductType, nextProductId, nextStyles, { colorId: nextColorId });
      }
    }

    // for client routing, this block is for calls we need to make once the product itself has loaded.
    if (newProductLoaded) {
      const style = ProductUtils.getStyleByColor(nextStyles, nextColorId);

      !isGiftCard && onLookupRewardsTransparencyPointsForItem();

      track(pvProduct, style);

      if (window.pageYOffset === 0 && !isDesktop(marketplace)) {
        this.slideUpHeader();
      }

      if (pdpConfig.showSizingPrediction && ProductUtils.isShoeType(nextProductType)) {
        fetchSizingPrediction(nextProductId, nextColorId);
      }

      if (isDesktop(marketplace)) { // desktop-only features
        if (pdpConfig.showBrandPromo) {
          fetchBrandPromo(nextDetail.brandId);
        }
      }

      this.checkForRecos(nextProductType, nextDetailProductId || nextProductId, nextStyles, { colorId: nextColorId });
    }

    if (!isSimilarStylesLoading) {
      this.checkForHearts(prevProps, this.props);
    }

    // Attempt to set approximate sizes when last selected sizes load from local storage, a new product is loaded, or a new color is selected
    const lastSelectedSizesDoneLoading = lastSelectedSizes && !Object.keys(prevProps.lastSelectedSizes).length && Object.keys(lastSelectedSizes).length;
    const shouldSetApproximateSizes = lastSelectedSizesDoneLoading || newProductLoaded || colorId !== nextColorId;
    if (hydraLastSelectedSize && pdpConfig.autoSelectApproximateSizes && shouldSetApproximateSizes) {
      this.handleSetApproximateSizes();
    }
  }

  componentWillUnmount() {
    const {
      isCartModalShowing, setLayoutOption, showCartModal, togglePdpVisibility
    } = this.props;
    const { isSpotlightActive } = this.state;
    togglePdpVisibility(false);
    isCartModalShowing && showCartModal(false);
    if (!PRODUCT_DETAIL_IMAGE_PAGE_RE.test(location.pathname)) {
      this.resetCarouselIndex();
    }

    setLayoutOption('showBackToTop', 0);

    // we probably don't need to do this, since the component is being unmounted let's cleanup
    if (isSpotlightActive) {
      this.zoomOut();
    }
  }

  getRecosToDisplay(slot, props) {
    const {
      similarProductRecos: recoStoreData,
      product: { detail },
      params
    } = props;

    const { colorId } = params || {};

    const {
      productId,
      styles
    } = detail || {};

    if (!styles) {
      return;
    }

    const style = ProductUtils.getStyleByColor(styles, colorId);
    const { lastReceivedRecoKey, janus: janusRecos = {} } = recoStoreData;
    const recosForProduct = janusRecos[buildProductPageRecoKey(productId, style?.styleId)] || janusRecos;
    return getRecosSlot(recosForProduct, slot, lastReceivedRecoKey);
  }

  handleSetApproximateSizes() {
    const {
      productSizeChanged,
      validateDimensions,
      lastSelectedSizes,
      params,
      product : {
        detail,
        selectedSizing
      }
    } = this.props;

    const {
      styles,
      sizing : {
        dimensionsSet,
        hypercubeSizingData,
        valuesSet,
        stockData
      }
    } = detail;
    const gender = ProductUtils.getGender(detail);

    const approximateSizes = dimensionsSet.reduce((selectedSizes, dimensionId) => {
      const lastSelectedSize = getLastSelectedSize(lastSelectedSizes, gender, dimensionId);
      const sizeOptions = Object.values(valuesSet[dimensionId])[0];
      const approximateSize = getApproximateSize(sizeOptions, lastSelectedSize, hypercubeSizingData);
      return approximateSize
        ? { ...selectedSizes, [dimensionId] : approximateSize }
        : selectedSizes;
    }, {});
    // do not auto select the approximate sizes unless they are in stock
    const style = ProductUtils.getStyleByColor(styles, params.colorId);
    const inStock = Boolean(ProductUtils.getStockBySize(stockData, style.colorId, { ...approximateSizes, ...selectedSizing }));
    if (Object.keys(approximateSizes).length && inStock) {
      // spread approximate sizes first so existing selections stay put
      productSizeChanged({ ...approximateSizes, ...selectedSizing });
      validateDimensions();
    }
  }

  checkForHearts(props, nextProps) {
    const dontForgetStyles = props.dontForget?.product?.styles;
    const nextDontForgetStyles = nextProps.dontForget?.product?.styles;

    const detail1 = this.getRecosToDisplay('slot1', props);
    const nextDetail1 = this.getRecosToDisplay('slot1', nextProps);
    const nextDetail1Recos = nextDetail1?.recos;

    const detail2 = this.getRecosToDisplay('slot2', props);
    const nextDetail2 = this.getRecosToDisplay('slot2', nextProps);
    const nextDetail2Recos = nextDetail2?.recos;

    const detail3 = this.getRecosToDisplay('slot3', props);
    const nextDetail3 = this.getRecosToDisplay('slot3', nextProps);
    const nextDetail3Recos = nextDetail3?.recos;

    if ((dontForgetStyles !== nextDontForgetStyles) ||
        (detail1?.recos !== nextDetail1Recos) ||
        (detail2?.recos !== nextDetail2Recos) ||
        (detail3?.recos !== nextDetail3Recos)) {
      const allRecos = [
        ...(nextDetail1Recos || []),
        ...(nextDetail2Recos || []),
        ...(nextDetail3Recos || []),
        ...(nextDontForgetStyles || [])
      ];
      this.getHeartCountsDebounced(allRecos);
    }
  }

  fetchData(productId) {
    const {
      fetchAsk,
      loadProductDetailPage,
      fetchProductReviews,
      fetchProductReviewsWithMedia,
      fetchRelatedProducts,
      params: { colorId, seoName },
      showAsk,
      showReviews,
      showSizeGroups,
      showDontForget
    } = this.props;
    const { isSpotlightActive } = this.state;

    if (isSpotlightActive) {
      this.zoomOut();
    }

    loadProductDetailPage(productId, { colorId, seoName, firePixel: true });

    if (showSizeGroups || showDontForget) {
      fetchRelatedProducts(productId);
    }

    if (showAsk) {
      fetchAsk({ productId });
    }

    if (showReviews) {
      fetchProductReviews(productId, 1, 0, false);
      fetchProductReviewsWithMedia(productId);
    }
  }

  // Trigger all the tests defined in TEST_PROP_TO_NAME, and return a map of component prop name to whether the user is in the treatment.
  assignTests = () => {
    const results = {};
    const { triggerAssignment } = this.props;
    Object.keys(TEST_PROP_TO_NAME).forEach(propName => {
      const result = triggerAssignment(TEST_PROP_TO_NAME[propName]);
      results[propName] = isInAssignment(result);
    });
    return results;
  }

  getHeartCountsDebounced(...args) {
    this.props.getHeartCounts(...args);
  }

  resetCarouselIndex() {
    const { setCarouselIndex } = this.props;
    setCarouselIndex(0);
  }

  slideUpHeader() {
    const interval = window.setInterval(() => {
      const summaryContainer = document.getElementById('productRecap');
      if (summaryContainer) {
        clearInterval(interval);
        summaryContainer.scrollIntoView();
      }
    }, 20);
  }

  checkForRecos(productType = '', productId, styles = [], { colorId }) {
    const { fetchProductPageRecos } = this.props;
    const productStyleId = (ProductUtils.getStyleByColor(styles, colorId) || {}).styleId;

    if (!productStyleId) {
      return;
    }

    const recoType = ProductUtils.shouldUseVisualRecos(productType);
    fetchProductPageRecos(productId, productStyleId, recoType !== NO_VIZ, this.shouldShowExternalReferrerRecos());
  }

  useSeoUrl(product, colorId) {
    this.context.router.replacePreserveAppRoot(this.buildSeoUrl(product, colorId));
  }

  buildSeoUrl(product, colorId) {
    const { search } = this.props.location;
    return `${buildSeoProductUrl(product, colorId)}${search ? search : ''}`;
  }

  showAndHighlightSelectSizeTooltip() {
    const { showSelectSizeTooltip, highlightSelectSizeTooltip } = this.props;
    showSelectSizeTooltip();
    highlightSelectSizeTooltip();
  }

  unhighlightAndHideSelectSizeTooltip() {
    const { hideSelectSizeTooltip, unhighlightSelectSizeTooltip } = this.props;
    unhighlightSelectSizeTooltip();
    hideSelectSizeTooltip();
  }

  setLastSelectedSizes() {
    const {
      setLastSelectedSize,
      product : {
        detail,
        selectedSizing
      }
    } = this.props;
    const { sizing : { hypercubeSizingData = {} } } = detail;

    const gender = ProductUtils.getGender(detail);
    Object.entries(selectedSizing).forEach(([dimensionId, sizeId]) => {
      const range = hypercubeSizingData[sizeId];
      if (setLastSelectedSize && gender && dimensionId && sizeId && range) {
        setLastSelectedSize(gender, dimensionId, sizeId, range.min, range.max);
      }
    });
  }

  onAddToCartOrBuyNow = (e, stockId, isBuyNow) => {
    const {
      addItemToCart,
      changeQuantity,
      navigateToCheckout,
      product,
      trackEvent,
      trackLegacyEvent,
      showCartModal
    } = this.props;
    const { detail, selectedSizing, colorId } = product;
    const style = ProductUtils.getStyleByColor(detail.styles, colorId);
    e.preventDefault();
    if (ProductUtils.isSizeSelectionComplete(detail.sizing, selectedSizing)) {
      trackLegacyEvent('CartAddItem', null, `stockId:${stockId}|styleId:${style.styleId}`);
      trackEvent('TE_PDP_ADDTOCART');
      const amethystEventBundle = { ...style, addedFrom: PRODUCT_PAGE };
      if (isBuyNow) {
        track(evBuyNow, amethystEventBundle);
      } else {
        track(evAddToCart, amethystEventBundle);
      }
      this.setLastSelectedSizes();
      changeQuantity({ items: [{ stockId: stockId, quantity: 1, quantityAddition: true }] }, { firePixel: true })
        .then(response => {
          const error = translateCartError(response);
          if (error) {
            trackEvent('TE_PDP_ADDTOCART_FAILURE');
            alert(error);
          } else {
            addItemToCart(); // monetate event
            if (isBuyNow) {
              // this has to be separate from `navigateToCheckout` for some reason otherwise the event doesn't fire?
              track(evProceedToCheckout, { pageType: PRODUCT_PAGE });
              navigateToCheckout(this.context.router);
            } else {
              showCartModal(true, stockId);
            }
          }
        });
    } else {
      this.handleAddToCartIncomplete(e, style, detail.sizing, selectedSizing);
    }
  }

  onAddToCart = e => {
    const { stockId } = e.target;
    this.onAddToCartOrBuyNow(e, stockId && stockId.value, false);
  };

  onBuyNow = e => {
    const { dataset: { stockId } } = e.target;
    this.onAddToCartOrBuyNow(e, stockId, true);
  };

  // re-usable handler for onAddToCart and onBuyNow cases where sizing selection is incomplete
  handleAddToCartIncomplete = (e, style, sizingData, selectedSizing) => {
    this.showAndHighlightSelectSizeTooltip();
    this.props.validateDimensions(true);
    const firstInvalidDimensionName = ProductUtils.getMissingDimensionName(selectedSizing, sizingData);
    const missingDimension = `${firstInvalidDimensionName}_dimension`.toUpperCase();
    track(evAddToCart, { ...style, incompleteAddToCart: true, missingDimension, addedFrom: 'PRODUCT_PAGE' });

    // Do not scroll or show the alert dialog on desktop
    if (!isDesktop(marketplace)) {
      if (this.buyBox) {
        this.buyBox.scrollIntoView(true);
      }
      // scroll into view is async, so we want the elements to be in view when they see this alert.
      setTimeout(() => {
        alert(`Please select ${indefiniteArticleSelector(firstInvalidDimensionName)} ${firstInvalidDimensionName}.`);
      }, 25);
    }
  }

  onAddToFavorites(event) {
    const {
      heartProduct,
      isCustomer,
      pdpConfig,
      product: { detail, colorId, selectedSizing },
      trackEvent
    } = this.props;

    const { currentTarget } = event;
    event.preventDefault();

    const style = ProductUtils.getStyleByColor(detail.styles, colorId);
    const { productId, styleId, price } = style;

    const eventData = {
      styleId,
      productId,
      incompleteAddToFavorites: false,
      price,
      addedFrom: 'PRODUCT_PAGE'
    };

    if (!ProductUtils.isSizeSelectionComplete(detail.sizing, selectedSizing)) {
      const firstInvalidDimensionName = ProductUtils.getMissingDimensionName(selectedSizing, detail.sizing);
      this.showNoSizeSelected(detail.sizing, selectedSizing);

      eventData.incompleteAddToFavorites = true;
      eventData.missingDimension = `${firstInvalidDimensionName}_dimension`.toUpperCase();

    } else { // Size selection complete
      const stockId = currentTarget.stockId.value;
      trackEvent('TE_PDP_ADDTOFAVORITES', stockId);
      // if they've logged in at least once and is not force refresh or is in favorites assignment
      if (isCustomer) {
        heartProduct({ subItemId: currentTarget.stockId.value }, () => {
          this.context.router.push('/account/favorites');
        });
      } else {
        currentTarget.action = pdpConfig.addToFavoritesDesktopAction;
        currentTarget.submit();
      }
    }

    track(evAddToFavorites, eventData);
  }

  getStyleId = () => {
    const {
      product: { detail, colorId }
    } = this.props;

    const { styleId } = ProductUtils.getStyleByColor(detail?.styles, colorId);
    return styleId;
  }

  onStockChange(styleId,selectedSizing, { label, value }) {
    const { product: { detail }, productSizeChanged, stockSelectionCompleted, trackEvent, validateDimensions, productSwatchChange } = this.props;
    const { colorId } = ProductUtils.getStyleMap(detail.styles)[styleId];
    const { sizing, productId } = detail;

    if (label === 'color') {
      productSwatchChange(colorId);
      this.context.router.replacePreserveAppRoot(this.buildSeoUrl(detail, colorId));
    } else {
      productSizeChanged(selectedSizing);
      validateDimensions();
    }

    trackEvent('TE_PDP_SELECTDIMENSION',`${label}:${value}`);

    if (ProductUtils.isSizeSelectionComplete(sizing, selectedSizing)) {
      stockSelectionCompleted({ colorId, productId, styleId, selectedSizing });
      this.unhighlightAndHideSelectSizeTooltip();
    }
  }

  onProductDescriptionToggle() {
    this.props.toggleProductDescription();
  }

  onShowReportError = event => {
    const { toggleReportAnErrorModal } = this.props;
    event.preventDefault();
    toggleReportAnErrorModal(true);
  }

  onRecoClicked({ currentTarget }) {
    const loc = parsePath(relativizeUrl(currentTarget.href));
    const { ref } = parse(loc.search);
    const source = ProductUtils.translateRecoNameToAnalyticsSource(ref);
    const productId = currentTarget.getAttribute('data-product-id');
    const index = currentTarget.getAttribute('data-index');
    const type = currentTarget.getAttribute('data-type');
    const widgetType = ProductUtils.translateRecoTitleToAmethystWidget(type);

    track(evRecommendationClick, {
      index,
      recommendationType: 'PRODUCT_RECOMMENDATION',
      recommendationValue: productId,
      recommendationSource: 'EP13N',
      widgetType: widgetType
    });

    this.props.trackEvent('TE_PDP_SIMILARITEM', `sku:${productId}:source:${source}:index:${index}`);
  }

  onOpenProductNotifyMe = event => {
    const { toggleProductNotifyModal, trackLegacyEvent } = this.props;
    event.preventDefault();
    trackLegacyEvent('Product-Page', 'OOS', 'Notify-Me');
    toggleProductNotifyModal(true);
  }

  onCloseProductNotifyMe = () => {
    this.props.toggleProductNotifyModal(false);
  }

  onOpenBrandNotify = event => {
    const { toggleBrandNotifyModal } = this.props;
    event.preventDefault();
    toggleBrandNotifyModal(true);
  }

  onCloseBrandNotify = () => {
    this.props.toggleBrandNotifyModal(false);
  }

  onOutofStockPopoverShown = () => {
    const {
      params: { productId, colorId },
      product: { detail: { styles } },
      fetchProductSearchSimilarity } = this.props;
    const style = ProductUtils.getStyleByColor(styles, colorId);
    if (style) {
      fetchProductSearchSimilarity(productId, style.styleId);
    }
  }

  showNoSizeSelected = (sizing, selectedSizing) => {
    const { validateDimensions } = this.props;

    this.showAndHighlightSelectSizeTooltip();
    validateDimensions(true);
    const firstInvalidDimensionName = ProductUtils.getMissingDimensionName(selectedSizing, sizing);

    // Do not show the alert dialog on desktop
    if (!isDesktop(marketplace)) {
      alert(`Please select a ${firstInvalidDimensionName}.`);
    }
  }

  handleThumbnailClick = () => {
    const { isSpotlightActive } = this.state;
    if (isSpotlightActive) {
      this.zoomOut();
    }
    trackLegacyEvent('Product-Page', 'PrImage', 'Thumbnail-Swap-Click');
  };

  handleProductImageClick = (lowResImageSrc, hiResImageSrc, lowResImageWidth, lowResImageHeight, mouseCoordinates) => {
    this.setState({
      isSpotlightActive: true,
      spotlightLowResImageSrc: lowResImageSrc,
      spotlightHiResImageSrc: hiResImageSrc,
      spotlightHiResImageWidth: lowResImageWidth * 4,
      spotlightHiResImageHeight: lowResImageHeight * 4,
      mouseCoordinates
    }, () => {
      this.setHeightsAndOffsets();
    });
  };

  setHeightsAndOffsets = () => {
    const { pdpConfig } = this.props;
    const productImages = document.querySelector(pdpConfig.spotlightProductImagesId);
    const productImagesOffsetTop = offset(productImages).top;
    const theaterOffsetTop = offset(this.theater).top;
    const spotlightWrapperHeight = productImagesOffsetTop - theaterOffsetTop;
    this.setState({ spotlightWrapperHeight }, () => {
      this.calcMovementRatios();
    });
  };

  handleSpotlightWrapperMouseMove = event => {
    this.moveSpotlight(event);
  };

  handleSpotlightWrapperClick = () => {
    this.zoomOut();
  };

  zoomOut = () => {
    this.setState({
      spotlightLowResImageSrc: null,
      spotlightHiResImageSrc: null,
      spotlightHiResImageWidth: 0,
      spotlightHiResImageHeight: 0,
      spotlightWrapperHeight: 0,
      movementRatioX: 0,
      movementRatioY: 0,
      mouseCoordinates: {},
      isSpotlightActive: false
    });
  };

  calcMovementRatios = () => {
    const { spotlightWrapperHeight } = this.state;
    const spotlightWrapperWidth = Math.abs(this.spotlightWrapper.offsetWidth);
    const { spotlightHiResImageWidth, spotlightHiResImageHeight, mouseCoordinates } = this.state;

    this.setState({
      movementRatioX: (spotlightHiResImageWidth - spotlightWrapperWidth) / spotlightWrapperWidth,
      movementRatioY: (spotlightHiResImageHeight - spotlightWrapperHeight) / spotlightWrapperHeight
    }, () => {
      this.moveSpotlight(mouseCoordinates);
    });
  };

  moveSpotlight = event => {
    const { movementRatioX, movementRatioY } = this.state;
    const relativeX = event.pageX - offset(this.spotlightWrapper).left;
    const relativeY = event.pageY - offset(this.spotlightWrapper).top;

    const spotlightLeft = relativeX * movementRatioX;
    const spotlightTop = relativeY * movementRatioY;

    this.spotlight.style.left = `${-spotlightLeft}px`;
    this.spotlight.style.top = `${-spotlightTop}px`;
  };

  makeSpotlight = () => {
    const {
      isSpotlightActive,
      spotlightWrapperHeight,
      spotlightLowResImageSrc,
      spotlightHiResImageSrc,
      spotlightHiResImageWidth,
      spotlightHiResImageHeight
    } = this.state;

    const style = { height: spotlightWrapperHeight || 0 };
    if (style.height === 0) {
      style.display = 'none';
    }

    return (
      <>
        <button
          type="button"
          ref={el => this.spotlightWrapper = el}
          style={style}
          className={cn(css.spotlightWrapper, { [css.active]: isSpotlightActive })}
          onMouseMove={this.handleSpotlightWrapperMouseMove}
          onClick={this.handleSpotlightWrapperClick}
          tabIndex={isSpotlightActive ? 0 : -1 }>
          <div ref={el => this.spotlight = el} className={css.spotlight}>
            {spotlightLowResImageSrc &&
              <img
                src={spotlightLowResImageSrc}
                className={css.spotlightLowResImage}
                width={spotlightHiResImageWidth}
                height={spotlightHiResImageHeight}
                alt="presentation"/>
            }
            {spotlightHiResImageSrc &&
              <img
                src={spotlightHiResImageSrc}
                className={css.spotlightHiResImage}
                width={spotlightHiResImageWidth}
                height={spotlightHiResImageHeight}
                alt="presentation"/>
            }
          </div>
        </button>
        <button
          type="button"
          className={css.spotClose}
          tabIndex={isSpotlightActive ? 0 : -1}
          onClick={this.handleSpotlightWrapperClick}
          aria-label="Zoom out of product image"/>
      </>
    );
  };

  makeFavoritesButton(style, sizing, selectedSizing) {
    const { pdpConfig, showSelectSizeTooltip, hydraSocialCollections } = this.props;
    return (
      hydraSocialCollections ?
        <SocialCollectionsWidget canAddNewCollection={true} getStyleId={this.getStyleId} /> :
        <AddToFavoritesButton
          action={pdpConfig.addToFavoritesMobileAction}
          colorId={style.colorId}
          onAddToFavorites={this.onAddToFavorites}
          sizing={sizing}
          selectedSizing={selectedSizing}
          showSelectSizeTooltip={showSelectSizeTooltip}
          hideSelectSizeTooltip={this.onHideSelectSizeTooltip}/>
    );
  }

  makeSharingButtons() {
    const { customerFirstName, product: { detail }, params: { productId, colorId } } = this.props;
    const { fbAppId } = links.sharing;
    const { testId } = this.context;
    const style = ProductUtils.getStyleByColor(detail.styles, colorId);
    const product = {
      link: ProductUtils.getSharingButtonLink(productId, colorId),
      name: `${detail.brandName} ${detail.productName}`,
      style: style.color,
      image: detail.defaultImageUrl
    };

    return (
      <div className={css.sharing}>
        <span className={css.sharePrefix}>Share:</span>
        <div className={css.sharingIcons} data-test-id={testId('shareSection')}>
          <FacebookShare className={css.facebook} appId={fbAppId} product={product}/>
          <TwitterShare className={css.twitter} product={product}/>
          <PinterestShare className={css.pinterest} product={product}/>
          <SmsShare className={css.sms} product={product}/>
          <EmailShare
            product={product}
            productId={productId}
            colorId={colorId || style.colorId}
            firstName={customerFirstName}
            marketplace={marketplace}/>
        </div>
      </div>
    );
  }

  onSwatchStyleChosen(event) {
    const { currentTarget } = event;
    const { isSpotlightActive } = this.state;
    const { product: { selectedSizing } } = this.props;
    const styleId = currentTarget.getAttribute('data-style-id');
    this.onStockChange(styleId, selectedSizing, { label: 'color', value: styleId });
    if (isSpotlightActive) {
      this.zoomOut();
    }
  }

  makeMobileSwatchPicker(productStyles, style, styleThumbnails, isGiftCard) {
    return !isGiftCard && (
      <div className={css.swatchPicker}>
        <MobileSwatchPicker
          productStyles={productStyles}
          selectedStyleId={style.styleId}
          onStyleChange={this.onSwatchStyleChosen}
          thumbnails={styleThumbnails} />
      </div>
    );
  }

  onHideSelectSizeTooltip() {
    const { product: { isSelectSizeTooltipHighlighted }, hideSelectSizeTooltip } = this.props;
    if (!isSelectSizeTooltipHighlighted) {
      hideSelectSizeTooltip();
    }
  }

  makeProductNotifyMe() {
    const { testId } = this.context;
    return (
      <div className={css.notifyMe}>
        <button
          type="button"
          className={css.notifyMeButton}
          onClick={this.onOpenProductNotifyMe}
          data-test-id={testId('notifyMe')}>Don&apos;t see your size?</button>
      </div>
    );
  }

  makeStylePicker({
    product,
    style,
    styleThumbnails,
    selectedSizing,
    dimensionValidation,
    sizingPredictionId,
    isOnDemandEligible
  }) {
    const {
      hydraBuyNow,
      location,
      product: {
        detail: { defaultImageUrl, brandName, productName, styles },
        isSelectSizeTooltipVisible,
        isSelectSizeTooltipHighlighted
      },
      isGiftCard,
      pdpConfig,
      showOOSNotifyMe,
      showSelectSizeTooltip,
      hydraSizeRecommenderTooltip
    } = this.props;
    if (style) {
      return (
        <div id="buyBox" className={css.stylePicker} ref={c => this.buyBox = c}>
          <div className={css.newStylePicker}>
            <StylePicker
              styleList={styles}
              productId={product.productId}
              productType={product.defaultProductType}
              productImage={defaultImageUrl}
              productTitle={`${brandName} ${productName}`}
              sizing={product.sizing}
              genders={product.genders}
              selectedSizing={selectedSizing}
              selectedStyle={style}
              thumbnails={styleThumbnails}
              dimensionValidation={dimensionValidation.dimensions}
              onStockChange={this.onStockChange}
              onAddToCart={this.onAddToCart}
              addToCartAction={pdpConfig.addToCartAction}
              isSelectSizeTooltipVisible={isSelectSizeTooltipVisible}
              isSelectSizeTooltipHighlighted={isSelectSizeTooltipHighlighted}
              onShowSelectSizeTooltip={showSelectSizeTooltip}
              onHideSelectSizeTooltip={this.onHideSelectSizeTooltip}
              onUnhighlightSelectSizeTooltip={this.unhighlightAndHideSelectSizeTooltip}
              showSizeGender={pdpConfig.showSizeGender}
              showSizeCharts={pdpConfig.showSizeCharts}
              sizingPredictionId={sizingPredictionId}
              isOnDemandEligible={isOnDemandEligible}
              hydraSizeRecommenderTooltip={hydraSizeRecommenderTooltip}
              hasRecommendedSizing={pdpConfig.hasRecommendedSizing}
              location={location} />
            {pdpConfig.showRewardsCopy && (
              <div className={css.rewardsCopy}>
                Join Zappos Rewards & get Free 2-Business Day shipping.
                <Link
                  to="/zappos-rewards/"
                  data-track-action="Product-Page"
                  data-track-label="PrForm"
                  data-track-value="Rewards">Enroll now</Link>
              </div>
            )}
            {pdpConfig.showBuyNow && hydraBuyNow && <BuyNowButton
              sizing={product.sizing}
              selectedSizing={selectedSizing}
              colorId={style.colorId}
              onClick={this.onBuyNow}
              onShowSelectSizeTooltip={showSelectSizeTooltip}
              onHideSelectSizeTooltip={this.onHideSelectSizeTooltip}
            />}
            {this.makeFavoritesButton(style, product.sizing, selectedSizing)}
          </div>
          {showOOSNotifyMe && !isGiftCard && this.makeProductNotifyMe()}
          {pdpConfig.showBrandNotifyMe && !isGiftCard && (
            <div className={css.brandNotifyMe}>
              <a
                href={`${desktopBaseUrl}/prd/popups/brandNotifyMe.zml?brandId=${product.brandId}&brandName=${product.brandName}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Click to follow this brand and receive new product updates!"
                onClick={this.onOpenBrandNotify}>Notify Me of New Styles</a>
            </div>
          )}
          {this.makeSharingButtons()}
        </div>
      );
    }
  }

  onBack = e => {
    e.preventDefault();
    this.context.router.goBack();
  };

  makeOutOfStock = styleThumbnails => {
    const {
      product: { detail, similarStyles, selectedSizing },
      brandPage, params, pdpConfig, submitNotifyBrandEmail,
      productNotify,
      showOOSNotifyMe,
      hydraOosm
    } = this.props;
    const { notifyEmail: { submitted } } = brandPage;
    const { productId, brandId, brandName, styles, sizing } = detail;
    const productStyles = ProductUtils.getStyleMap(styles);
    const style = ProductUtils.getStyleByColor(styles, params.colorId);
    const stock = ProductUtils.getStockBySize(sizing.stockData, style.colorId, selectedSizing);
    if (pdpConfig.showOutOfStockPopover) {
      return (
        <OutOfStockPopover
          brandId={brandId}
          brandName={brandName}
          recos={similarStyles}
          stock={stock}
          isSubmitted={submitted}
          onBrandNotifySubmit={submitNotifyBrandEmail}
          onShow={this.onOutofStockPopoverShown}/>
      );
    } else if (showOOSNotifyMe) {
      return (
        <OutOfStockModal
          detail={detail}
          productId={productId}
          style={style}
          sizing={sizing}
          selectedSizing={selectedSizing}
          productStyles={productStyles}
          thumbnails={styleThumbnails}
          onSwatchStyleChosen={this.onSwatchStyleChosen}
          onStockChange={this.onStockChange}
          onOpenProductNotifyMe={this.onOpenProductNotifyMe}
          isProductNotifyOpen={productNotify.modalShown}
          hydraOosm={hydraOosm}
        />
      );
    } else {
      return null;
    }
  };

  makeBrandLogo({ brand }, secureImageBaseUrl) {
    const { id, name, headerImageUrl } = brand;
    const { testId } = this.context;
    if (headerImageUrl) {
      const imgProps = {
        src: `${secureImageBaseUrl}${headerImageUrl}`,
        alt: name,
        itemProp: 'logo'
      };
      const placeholder = <div className={css.brandPlaceholder} />;
      return (
        <Link
          to={buildSeoBrandString(name, id)}
          title={name}
          data-track-action="Product-Page"
          data-track-label="Tabs"
          data-track-value="Brand-Logo"
          data-test-id={testId('brandLogo')}>
          <ImageLazyLoader
            imgProps={imgProps}
            placeholder={placeholder}
          />
        </Link>
      );
    } else {
      return (
        <p className={css.brandName}>
          <Link to={buildSeoBrandString(name, id)}>{name}</Link>
        </p>
      );
    }
  }

  makeVideoInDescription(videos = [], productId, isYouTubeVideo, youtubeSrc) {
    const video = videos.find(video => video.videoEncodingExtension === 'mp4');
    if (video) {
      const videoSrcUrl = isYouTubeVideo ? youtubeSrc : `${desktopBaseUrl}${video.filename}`;
      const slotDetails = {
        src: videoSrcUrl,
        productId,
        heading: 'Product Video',
        isEmbedded: isYouTubeVideo
      };
      const videoProps = {
        heightValue: '100%',
        isYouTubeVideo,
        slotDetails,
        widthValue: '100%'
      };
      return (
        <div className={css.descriptionVideo}>
          <MelodyVideoPlayer {...videoProps} />
        </div>
      );
    }
  }

  makeDontForget = heartsData => {
    const { dontForget, showDontForget } = this.props;
    let contents;
    if (showDontForget && dontForget) {
      contents = <DontForget {...dontForget} heartsData={heartsData} />;
    }
    return <div className={css.dontForget}>{contents}</div>;
  };

  handleReviewMediaClick = (reviewId, mediaIndex) => {
    const { showReviewGalleryModal } = this.props;
    showReviewGalleryModal(reviewId, mediaIndex);
  };

  makeAsk() {
    const { showAsk, params } = this.props;
    if (showAsk) {
      return <Ask params={params} />;
    }
    return null;
  }

  makeSizeGroups() {
    const {
      params: { colorId: colorIdFromUrl },
      product: { detail: { styles } },
      showSizeGroups, sizeGroups
    } = this.props;
    const { colorId } = ProductUtils.getStyleByColor(styles, colorIdFromUrl);
    if (showSizeGroups) {
      return <SizeGroups
        {...sizeGroups}
        currentColorId={colorId} />;
    }
    return null;
  }

  shouldShowExternalReferrerRecos() {
    const { pageView } = this.props;
    const { externalReferrer } = this.state;
    const isInitialPageView = pageView && !pageView.clientRoutedUrls.length && !pageView.needsToFire;
    return showRecosOnTopForExternalReferrer && externalReferrer && isInitialPageView;
  }

  makeReviewPhotoGallery = ({ limitOverride, name, showMediaCount, includeHr, divClass, viewAllText, id = null }) => {
    const {
      props: { reviewGallery, pdpConfig, showReviews },
      handleReviewMediaClick
    } = this;
    if (!showReviews || !shouldRenderReviewGallery(reviewGallery)) {
      return null;
    }
    return (
      <ReviewPhotoGallery
        id={id}
        divClass={divClass}
        includeHr={includeHr}
        reviewGallery={reviewGallery}
        limit={typeof limitOverride === 'undefined' ? pdpConfig[`${name}MediaReviewsLimit`] : limitOverride}
        placement={name}
        onOpenMediaReview={handleReviewMediaClick}
        showMediaCount={showMediaCount}
        viewAllText={viewAllText} />
    );
  };

  makeReviewPhotoGalleryAisle = () => this.makeReviewPhotoGallery({
    name: 'aisle',
    showMediaCount: true,
    includeHr: true,
    divClass: css.reviewPhotoGalleryAisle,
    id: 'photoVideo'
  });

  makeReviewPhotoGalleryBottom = () => {
    const { makeReviewPhotoGallery } = this;
    const opts = {
      name: 'bottom',
      showMediaCount: false,
      includeHr: true,
      divClass: css.reviewPhotoGalleryBottom,
      viewAllText: 'View All Customer Photos and Videos',
      limitOverride : 0
    };
    return makeReviewPhotoGallery(opts);
  }

  render() {
    const {
      params,
      pdpConfig,
      product,
      reportAnError,
      reviewGallery,
      productNotify,
      secureImageBaseUrl,
      pageLoaded,
      p13nPixelUrl,
      origin,
      location: { pathname, search },
      showMelodyShippingAndReturnsBanner,
      numberOfReviews,
      numberOfAskQuestions,
      similarProductRecos,
      isCustomer,
      heartProduct,
      hearts,
      products,
      toggleHeartingLoginModal,
      trackEvent,
      unHeartProduct,
      metaDescription,
      sizeGuide,
      hydraSizeChart,
      isGiftCard,
      hydraProductCallout
    } = this.props;

    const {
      brandPromo,
      carouselIndex,
      detail,
      styleThumbnails,
      selectedSizing,
      validation,
      sizingPredictionId,
      isOnDemandEligible,
      isDescriptionExpanded
    } = product;

    const isLoaded = ProductUtils.isProductDataLoaded(product, params);

    if (!isLoaded) {
      return <PageLoader />;
    }

    const {
      defaultProductType,
      description,
      productId,
      styles,
      videos,
      sizeFit,
      widthFit,
      archFit,
      reviewSummary,
      youtubeVideoId,
      youtubeData : {
        embedUrl,
        contentUrl,
        videoName,
        thumbnailUrl,
        uploadDate
      }
    } = detail;

    const { isSpotlightActive } = this.state;
    const productStyles = ProductUtils.getStyleMap(styles);
    const style = ProductUtils.getStyleByColor(styles, params.colorId);
    const styleId = style && style.styleId;
    const multiviewImages = ProductUtils.buildAngleThumbnailImageUrls(style);
    const isYouTubeVideo = !!youtubeVideoId;
    const isProductDescriptionExpanded = !!isDescriptionExpanded;
    const heartProps = {
      hasHearting,
      isCustomer,
      heartProduct,
      hearts,
      products,
      toggleHeartingLoginModal,
      trackEvent,
      unHeartProduct
    };
    const heartsData = getHeartProps(heartProps, { addedFrom: 'PRODUCT_PAGE', heartEventName: 'TE_PDP_HEART', unHeartEventName: 'TE_PDP_UNHEART' });

    return (
      <SiteAwareMetadata loading={!isLoaded}>
        {/* data-pdp-style-id is to allow marketing to easily access the style-id */}
        <div data-pdp-style-id={styleId} className={css.wrap}>
          {this.shouldShowExternalReferrerRecos() && showCustomersWhoViewedThisItemAlsoViewed &&
            <RecosDetail3
              styleId={styleId}
              params={params}
              onRecoClicked={this.onRecoClicked}
              similarProductRecos={similarProductRecos}
              heartsData={heartsData} />
          }
          <ProductBreadcrumbs
            product={detail}
            onBack={this.onBack}
            showBreadcrumbsSku={pdpConfig.showBreadcrumbsSku} />
          <div
            ref={el => this.theater = el}
            className={css.theater}
            itemScope
            itemType="http://schema.org/Product"
            data-test-id={this.context.testId('productDetail')}>
            <div className={css.primaryWrapper}>
              <div className={css.productStage} id="productRecap">
                <div id="stage" className={css.productMain}>
                  <ProductSummary
                    product={detail}
                    style={style}
                    percentOffText={pdpConfig.percentOffText}
                    showPercentOffBanner={pdpConfig.showPercentOffBanner}
                    showReviewSummary={showReviews}/>
                  {multiviewImages && multiviewImages.length ?
                    <ProductImages
                      images={multiviewImages}
                      onThumbnailClick={this.handleThumbnailClick}
                      onProductImageClick={this.handleProductImageClick}
                      carouselIndex={carouselIndex}
                      productId={params.productId}
                      productType={detail.defaultProductType}
                      colorId={params.colorId}
                      styleId={styleId}
                      videos={videos}
                      productStyles={productStyles}
                      style={style}
                      styleThumbnails={styleThumbnails}
                      isYouTubeVideo={isYouTubeVideo}
                      youtubeSrc={embedUrl}
                      onStyleChange={this.onSwatchStyleChosen}
                      makeSpotlight={this.makeSpotlight}
                      isSpotlightActive={isSpotlightActive}/> : null}
                  {this.makeMobileSwatchPicker(productStyles, style, styleThumbnails, isGiftCard)}
                  { hydraProductCallout && pdpConfig.showProductCallout && <ProductCallout attributes={style.taxonomyAttributes} /> }
                </div>
                <div className={css.productForm}>
                  <aside className={css.wing}>
                    <h2 className={css.ariaHidden}>Product Info</h2>
                    <div className={css.wingInfo}>
                      <Price
                        productStyle={style}
                        percentOffText={pdpConfig.percentOffText}
                        showPercentOffBanner={pdpConfig.showPercentOffBanner}/>
                      {pdpConfig.showFreeShipping && <span className={css.freeShipping}>Ships Free!</span>}
                    </div>
                    {this.makeStylePicker({
                      product: detail,
                      style,
                      styleThumbnails,
                      selectedSizing,
                      dimensionValidation: validation,
                      sizingPredictionId,
                      isOnDemandEligible
                    })}
                    {this.makeDontForget(heartsData)}
                    <div>
                      <RecosDetail1
                        styleId={styleId}
                        numberOfAskQuestions={numberOfAskQuestions}
                        numberOfReviews={numberOfReviews}
                        params={params}
                        onRecoClicked={this.onRecoClicked}
                        similarProductRecos={similarProductRecos}
                        heartsData={heartsData}
                      />
                    </div>
                    {this.makeReviewPhotoGalleryAisle()}
                  </aside>
                </div>
                {pdpConfig.showFitSurvey && <FitSurvey
                  sizeFit={sizeFit}
                  widthFit={widthFit}
                  archFit={archFit}
                  reviewSummary={reviewSummary}/>
                }
                <meta itemProp="category" content={defaultProductType}/>
                { ProductUtils.isShoeType(defaultProductType) && hydraSizeChart && <SingleSizeChart
                  sizeGuide={sizeGuide}
                  gender={detail.genders} /> }

                <div id="itemInformation" className={css.descriptionWrapper}>
                  <ExpandableProductDescription
                    productId={productId}
                    colorId={params.colorId}
                    defaultProductType={defaultProductType}
                    descriptionItems={description}
                    allowCollapse={showReviews}
                    isExpanded={isProductDescriptionExpanded}
                    onToggle={this.onProductDescriptionToggle}
                    onReportError={this.onShowReportError}
                    brandLogo={this.makeBrandLogo(detail, secureImageBaseUrl)}/>
                  {this.makeSizeGroups()}
                  <RecosDetail2
                    styleId={styleId}
                    params={params}
                    onRecoClicked={this.onRecoClicked}
                    similarProductRecos={similarProductRecos}
                    heartsData={heartsData}
                  />
                </div>

                {this.makeAsk()}
                {pageLoaded && this.makeVideoInDescription(videos, productId, isYouTubeVideo, embedUrl)}

                <ReviewPreview
                  params={params}
                  onReviewMediaClick={this.handleReviewMediaClick} />
                {showReviews && shouldRenderReviewGallery(reviewGallery) ? (
                  <ReviewGallery params={params} returnUrl={buildSeoProductUrl(detail)}/>
                ) : null}
                {this.makeReviewPhotoGalleryBottom()}
                <div className={css.outOfStock}>
                  {this.makeOutOfStock(styleThumbnails)}
                </div>
              </div>
            </div>
            {!this.shouldShowExternalReferrerRecos() && showCustomersWhoViewedThisItemAlsoViewed &&
              <RecosDetail3
                styleId={styleId}
                params={params}
                onRecoClicked={this.onRecoClicked}
                similarProductRecos={similarProductRecos}
                heartsData={heartsData} />
            }
          </div>
          {showSimilarItemsYouMayLike &&
            <RecosDetail3
              styleId={styleId}
              params={params}
              onRecoClicked={this.onRecoClicked}
              similarProductRecos={similarProductRecos}
              heartsData={heartsData} />
          }
          {(brandPromo && brandPromo.type) ? <BrandPromo data={brandPromo} /> : null}
          {showMelodyShippingAndReturnsBanner && <ShippingAndReturnsBanner />}
        </div>
        {productNotify.modalShown && <ProductNotifyModal
          isOpen={productNotify.modalShown}
          onClose={this.onCloseProductNotifyMe}
          product={detail}
          productId={productId}
          colorId={params.colorId}
          selectedSizing={selectedSizing}/>}
        {productNotify.brandModalShown && <BrandStylesNotifyModal
          isOpen={productNotify.brandModalShown}
          onClose={this.onCloseBrandNotify}
          brandId={detail.brandId}
          brandName={detail.brandName}/>}
        {reportAnError.modalShown && <ReportAnErrorModal
          productId={productId}
          colorId={style && style.colorId}
          isOpen={reportAnError.modalShown}/>}
        <SessionRequired>
          <img alt="" src={`${p13nPixelUrl}?widget=RecordViewedItem&item=${productId}&referer=${encodeURIComponent(`${origin}${pathname}${search}`)}`} className={css.janusIngestionPixel}/>
        </SessionRequired>
        {
          isYouTubeVideo
          && <VideoObject
            name={videoName}
            embedUrl={embedUrl}
            contentUrl={contentUrl}
            description={metaDescription}
            thumbnailUrl={thumbnailUrl}
            uploadDate={uploadDate} />
        }
      </SiteAwareMetadata>
    );
  }
}

export function mapStateToProps(state) {
  const isCustomer = !!(ExecutionEnvironment.canUseDOM && state.cookies['x-main']);
  const {
    cart: { isModalShowing: isCartModalShowing },
    reviews: { reviewGallery },
    sharing: { productNotify, reportAnError },
    hearts: heartsStyleIdsList = {},
    ask, brandPage, dontForget, environmentConfig, holmes,
    localStorage = {},
    meta,
    pageLoad: { loaded: pageLoaded },
    pageView,
    product,
    products, recos, sizeGroups, url,
    headerFooter
  } = state;
  const metaDescription = meta.documentMeta?.meta?.name?.description || ''; // to use as the SEO description for video as Broadway used to
  const customerFirstName = holmes && holmes.firstName || '';
  const sizeGuide = headerFooter.content.Global || '';
  const { imageServer } = environmentConfig;
  const {
    pdp,
    features: { showMelodyShippingAndReturnsBanner }
  } = marketplace;
  const testAssignments = {};
  Object.keys(TEST_PROP_TO_NAME).map(propName => {
    testAssignments[propName] = isAssigned(TEST_PROP_TO_NAME[propName], 1, state);
  });
  const { lastSelectedSizes } = localStorage;
  return {
    brandPage,
    isCustomer,
    customerFirstName,
    dontForget,
    isCartModalShowing,
    ...testAssignments,
    lastSelectedSizes,
    metaDescription,
    numberOfAskQuestions: getNumberOfAskQuestions(ask),
    numberOfReviews: ProductUtils.getNumberOfReviews(product),
    similarProductRecos: recos,
    pageLoaded,
    pageView,
    pdpConfig: pdp,
    product,
    products,
    productNotify,
    reportAnError,
    reviewGallery,
    secureImageBaseUrl: imageServer.url,
    showAsk: showAskConstant,
    showDontForget: showDontForgetConstant,
    showOOSNotifyMe: showOOSNotifyMeFeature,
    showSizeGroups: showSizeGroupsConstant,
    showMelodyShippingAndReturnsBanner,
    showReviews,
    sizeGroups,
    url,
    p13nPixelUrl: `${state.environmentConfig.api.mafia.url}/janus/recos/bi/ingest/pixel.png`,
    origin: state.environmentConfig.canonical.url,
    hearts: heartsStyleIdsList.heartsStyleIds,
    sizeGuide,
    isGiftCard: ProductUtils.isGiftCard(product?.detail?.defaultProductType)
  };
}

ProductDetail.defaultProps = {
  trackEvent,
  trackLegacyEvent
};

ProductDetail.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func
};

export const mapDispatchToProps = {
  changeQuantity,
  fetchAsk,
  fetchRelatedProducts,
  loadProductDetailPage,
  fetchProductSearchSimilarity,
  fetchProductPageRecos,
  fetchProductReviews,
  fetchProductReviewsWithMedia,
  fetchSizingPrediction,
  fetchBrandPromo,
  fetchCustomerAuthDetails,
  heartProduct,
  unHeartProduct,
  navigateToCheckout,
  pageTypeChange,
  productSizeChanged,
  togglePdpVisibility,
  toggleProductDescription,
  submitNotifyBrandEmail,
  showSelectSizeTooltip,
  hideSelectSizeTooltip,
  highlightSelectSizeTooltip,
  unhighlightSelectSizeTooltip,
  validateDimensions,
  setCarouselIndex,
  setLastSelectedSize,
  setProductDocMeta,
  stockSelectionCompleted,
  toggleBrandNotifyModal,
  toggleHeartingLoginModal,
  toggleProductNotifyModal,
  toggleReportAnErrorModal,
  showReviewGalleryModal,
  showCartModal,
  triggerAssignment,
  addItemToCart,
  productSwatchChange,
  getHeartCounts,
  getHearts,
  onLookupRewardsTransparencyPointsForItem
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetail);
